import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IGenericError,
  IQuestionReport,
  IQuestionReportBase,
  IQuestionReportRaw,
} from '../types'

const questionsReportsCollectionName = 'questionsReports'

const questionsReportsCollectionRef = collection(
  firebaseDb,
  questionsReportsCollectionName,
)

const questionsReportsDocRef = (id: string) =>
  doc(firebaseDb, questionsReportsCollectionName, id)

export async function createQuestionReport(
  createObj: IQuestionReportBase,
): Promise<string> {
  try {
    const document = await addDoc(questionsReportsCollectionRef, {
      ...createObj,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return document.id
  } catch (error: unknown) {
    console.error(
      `error while trying to create question report :>> `,
      (error as IGenericError)?.message || error,
      createObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to question report`,
    )
  }
}

export async function getQuestionReportById(
  id: string,
): Promise<IQuestionReport | undefined> {
  try {
    const docRef = questionsReportsDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as DocumentSnapshot<IQuestionReportRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IQuestionReport = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while trying to get question report by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get question report`,
    )
  }
}

export async function getQuestionReports(
  queryConstraints: QueryConstraint[] = [],
): Promise<IQuestionReport[]> {
  try {
    const q = query(questionsReportsCollectionRef, ...queryConstraints)
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IQuestionReportRaw>

    let documents: IQuestionReport[] = []

    querySnapshot.forEach((snapshot) => {
      documents.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(
      `error while trying to get question reports :>> `,
      (error as IGenericError)?.message || error,
      queryConstraints,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get question reports`,
    )
  }
}

export async function updateQuestionReportById(
  id: string,
  updateObj: Partial<IQuestionReport>,
): Promise<void> {
  try {
    const docRef = questionsReportsDocRef(id)

    await updateDoc(docRef, { ...updateObj, updatedAt: serverTimestamp() })
  } catch (error: unknown) {
    console.error(
      `error while trying to update question report by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update question report`,
    )
  }
}

export async function deleteQuestionReportById(id: string): Promise<void> {
  try {
    const docRef = questionsReportsDocRef(id)

    await deleteDoc(docRef)
  } catch (error: unknown) {
    console.error(
      `error while trying to delete question report by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete question report`,
    )
  }
}
